<template>
    <SectionContentLayout
      content_class="content list-group"
      :state="contacts_controller.state.events['fetch_foreground']"
      invalid_text='contacts.person.invalid'
      invalid_icon='fas fa-address-card'
      error_text="contacts.list.error"
      @errorRetry="emit('errorRetry')"
    >
        <template #header>
          <ContentTitle
            title="contacts.title"
          />
        </template>

        <template #actions_success v-if="contacts_selection.count === 0">
            <ActionButton
                @click="onReload()"
                v-tooltip='"contacts.actions.refresh"'
                :icon="contacts_controller.state['fetch_background'] === 'loading' ? 'fas fa-redo-alt fa-spin' : 'fas fa-redo-alt'"

            />
            <ActionButton
                @click="onSearchToggle()"
                v-tooltip='"contacts.actions.search"'
                icon='fas fa-search'
                :class="{'active' : search_visible && (!filter_visible && !more_actions_visible)}"
            />
            <ActionButton
                @click="onFilterToggle()"
                v-tooltip='"contacts.actions.filter"'
                icon='fas fa-sort-amount-down'
                class="filter"
                :class="{'active dropdown-active' : filter_visible}"
            />

            <MoreActionsDropdown
              @moreActionsOpen="onMoreActionsOpen"
              @moreActionsClose="onMoreActionsClose"
            >
              <template #actions>
                <ContactsContextActions
                  zone="contatcts_list_actions"
                />
              </template>
           </MoreActionsDropdown>
        </template>

        <template #actions_over_success v-else>
            <ActionButton
              v-if="contacts_selection.emails_selected.count"
              @click="onActionToSendMail()"
              v-tooltip='"contacts.actions.send_mail"'
              icon='fas fa-pencil-alt'
            />

            <ActionButton v-if="!contacts_selection.groups_count"
              @click="onSelectedToGroup()"
              v-tooltip='"contacts.actions.add_to_group"'
              icon='fas fa-users'
            />

            <MoreActionsDropdown>
              <template #actions>
                <ActionButton
                  v-if="contacts_selection.emails_selected.count"
                  @click=onActionAddFilter
                  text='contacts.actions.add_filter'
                  icon='fas fa-filter'
                />
                <ActionButton
                    @click="onSelectedContactsDelete()"
                    text='contacts.actions.delete'
                    icon='fas fa-trash-alt'
                />
              </template>
           </MoreActionsDropdown>
        </template>

        <template #pre_content_success>
            <SectionSubHeader v-if="active_filter">
                <template #content>
                    {{Language.say('contacts.list.results_filter')}}
                    <strong>{{Language.say(`contacts.list.filter_${active_filter}`)}}</strong>
                </template>

                <template #pos_content>
                    <ActionButton
                      @click='onFilterClear()'
                      icon='fas fa-times-circle'
                      class="outline"
                      text="contacts.list.filter_reset"
                    />
                </template>
            </SectionSubHeader>

            <InputSearch v-if="search_visible"
                ref="input_search_component"
                class="contacts-search"
                @inputSearchSet='onSearchSet'
                @inputSearchClear='onSearchClear'
                @inputSearchEscape='onSearchClose()'
            />

            <ContactsFilter v-if="filter_visible"
                @filterClear="onFilterClear"
                @filterSet="onFilterSet"
                @filterClose=""
            />
        </template>

        <template #content_success>
            <ContactsListRouter v-if="contacts_filter.count"
              ref="contacts_list_component"
              :contacts_filter=contacts_filter
              :contacts_selection=contacts_selection
              @contactContextMenu="onContactsContextMenu"
              @contactClick="onContactClick"
              @contactListLoaded="onScrollAfterRefresh"
            />
            <ContentEmpty v-else
              icon="far fa-address-card"
              text='contacts.list.empty_results'
            />
        </template>

        <template #content_loading>
          <ContactsListLoading />
        </template>
    </SectionContentLayout>

    <ContextMenu ref="context_menu_component">
        <template #menu_items>
            <ContactsContextSelectedActions
              :webmail_view="webmail_view"
              :contacts_view="contacts_view"
              :contacts_selection="contacts_selection"
            />
        </template>
    </ContextMenu>
</template>

<script setup>
  import {ref, reactive, watch, nextTick} from 'vue';
  import {onMounted, onUnmounted}         from 'vue';
  import {defineEmits}                    from 'vue';
  import {useRoute}                       from 'vue-router';
  import ContactsFilter                   from '../Partials/ContactsFilter.vue';
  import ContactsContextActions           from '../Partials/ContactsContextActions.vue';
  import ContactsListRouter               from '../Partials/ContactsListRouter.vue';
  import ContactsListLoading              from '../Partials/ContactsListLoading.vue';
  import ContactsContextSelectedActions   from '../Partials/ContactsContextSelectedActions.vue';
  import SectionContentLayout             from '@/vue/Layouts/Sections/SectionContentLayout.vue';
  import SectionSubHeader                 from '@/vue/Layouts/Sections/SectionSubHeader.vue';
  import ActionButton                     from '@/vue/Elements/Buttons/ActionButton.vue';
  import MoreActionsDropdown              from '@/vue/Elements/DropDowns/MoreActionsDropdown.vue';
  import InputSearch                      from '@/vue/Elements/Inputs/InputSearch.vue';
  import ContentEmpty                     from '@/vue/Elements/Content/ContentEmpty.vue';
  import ContextMenu                      from '@/vue/Elements/Tools/ContextMenu.vue';
  import ContentTitle                     from '@/vue/Elements/Content/ContentTitle.vue';
  import ContactsController               from '@/js/Contacts/ContactsController';
  import ContactsFilterCollection         from '@/js/Contacts/Collections/ContactsFilterCollection';
  import Language                         from '@/js/Shared/Language/Language';

  const emit = defineEmits([
    'errorRetry'
  ])

  const props = defineProps({
    contacts_view     : {},
    contacts_selection: {},
    webmail_view      : {},
  });

  const contacts_controller     = ContactsController;
  const contacts_filter         = reactive(new ContactsFilterCollection());
  const route                   = useRoute();
  const contacts_list_component = ref();
  const context_menu_component  = ref();
  const more_actions_visible    = ref(false);
  const filter_visible          = ref(false);
  const search_visible          = ref(false);
  const active_filter           = ref();
  const valid_filters           = {
    'persons' : true,
    'groups'  : true,
  }

  watch(() => route.query.filter, (filter) => {
    setActiveFilter(filter);
    setFilterList();
  });

  onMounted(() => {
    contacts_controller.state.on('fetch_foreground', 'success', onLoad);
    contacts_controller.broadcaster.on('contacts-added', onContactsAdded);
    contacts_controller.broadcaster.on('contacts-reload', onContactsAddedModal);
    contacts_controller.broadcaster.on('contacts-deleted', onContactsDeleted);
  })

  onUnmounted(() => {
    contacts_controller.state.destroy('fetch_foreground', 'success', onLoad);
    contacts_controller.broadcaster.destroy('contacts-added', onContactsAdded);
    contacts_controller.broadcaster.destroy('contacts-reload', onContactsAddedModal);
    contacts_controller.broadcaster.destroy('contacts-deleted', onContactsDeleted)
  })

  const onLoad = () => {
    setActiveFilter(route.query.filter);
    setFilterList();
  }

  const onReload = async () => {
    contacts_controller.fetchContactsBackground();
  }

  const onContactsAdded = async (contact) => {
    contacts_filter.reload();
    setSearchVisibility(false);
    setFilterVisibility(false);
    await nextTick();
    contacts_list_component.value.scrollToContact(contact);
  }

  const onContactsAddedModal = (contact) => {
    contacts_filter.reload();
  }

  const onContactsDeleted = () => {
    contacts_filter.reload();
    props.contacts_selection.reset();
  }

  const onContactsContextMenu = (contact) => {
    props.contacts_selection.unique(contact);
    context_menu_component.value.show();
  }

  const onContactClick = (contact) => {
    props.contacts_selection.reset();
    props.webmail_view.routeTo('/contacts/' + contact.type + '/info/' + contact.id);
  }

  const onSearchToggle = () => {
    (search_visible.value) ? onSearchClose() : onSearchOpen();
  }

  const onSearchOpen = () => {
    setFilterVisibility(false);
    setSearchVisibility(true);
  }

  const onSearchClose = () => {
    props.contacts_selection.reset();
    setSearchVisibility(false);
    setFilterList();
  }

  const onSearchSet = (input) => {
    props.contacts_selection.reset();
    contacts_filter.resetModifiers();
    contacts_filter.reload();
    contacts_filter.searchIn(active_filter.value || 'all', input)
  }

  const onSearchClear = () => {
    props.contacts_selection.reset();
    setFilterList();
  }

  const onFilterToggle = () => {
    setFilterVisibility(!filter_visible.value);
  }

  const onFilterSet = async (filter) => {
    props.webmail_view.routeTo('/contacts', {'filter' : filter});
    props.contacts_selection.reset();
    setFilterVisibility(false);
    setSearchVisibility(false);
    await nextTick();
    contacts_list_component.value.scrollToTop();
  }

  const onFilterClear = () => {
    props.webmail_view.routeTo('/contacts', {});
    props.contacts_selection.reset();
    setFilterVisibility(false);
    setSearchVisibility(false);
  }

  const onActionAddFilter = () => {
    props.contacts_view.onActionAddFilter();
  }

  const onScrollAfterRefresh = () => {
    if (route.params.contact_id) {
      setScrollAfterRefresh();
    }
  }

  const onMoreActionsOpen = () => {
    setMoreActionsVisibility(true);
    setFilterVisibility(false);
  }

  const onMoreActionsClose = () => {
    setMoreActionsVisibility(false);
  }

  const onSelectedToGroup = () => {
    props.contacts_view.openContactsSelectedToGroup(
      props.contacts_selection
    );
  }

  const onSelectedContactsDelete = () => {
    props.contacts_view.openContactsDelete(
      props.contacts_selection
    );
  }

  const setFilterList = () => {
    contacts_filter.reset();
    contacts_filter.setSearchList(contacts_controller.contacts.getList(active_filter.value));
    contacts_filter.setModifiers({
      'add_contact_letters' : true,
    });

    contacts_filter.reload();
  }

  const setActiveFilter = (value) => {
    active_filter.value = (valid_filters[value]) ? value : null;
  }

  const setFilterVisibility = (value) => {
    filter_visible.value = value;
  }

  const setMoreActionsVisibility = (value) => {
    more_actions_visible.value = value;
  }

  const setSearchVisibility = (value) => {
    search_visible.value = value;
  }

  const setScrollAfterRefresh = async () => {
      const contact = contacts_controller.contacts.all.get(route.params.contact_id);
      contacts_list_component.value.scrollToContact(contact);
  }

  const onActionToSendMail = () => {
    const recipients = props.contacts_selection.getRecipients();
    props.contacts_view.onActionToSendMail(recipients);
  }
</script>